import axios from "axios";

const BASE_API_URL = "/api/v1";

/**
 * Fetch questions for a video lesson
 * @param {number} videoLessonId - The ID of the video lesson
 * @param {string} questionType - Type of questions: "translation_match", "open_ended", "yes_no", "word_order"
 * @returns {Promise<Object>} Questions data
 */
export async function getVideoLessonQuestions(videoLessonId, questionType = "translation_match") {
  const path = `/videoLesson/${videoLessonId}/questions/?question_type=${questionType}`;

  // Try fetching without auth cookies (this endpoint is public)
  // credentials:'omit' prevents browser from sending session cookies that cause 403
  try {
    const response = await fetch(`${BASE_API_URL}${path}`, { credentials: 'omit' });
    if (response.ok) {
      const data = await response.json();
      return data;
    }
    // If 403 (e.g. CRA proxy forwards cookies server-side), try direct backend URL
    if (response.status === 403) {
      const directUrl = `https://staging-api.ailingua.net/api/v1${path}`;
      const directResponse = await fetch(directUrl);
      if (directResponse.ok) {
        const data = await directResponse.json();
        return data;
      }
    }
    throw new Error(`HTTP ${response.status}`);
  } catch (error) {
    console.error(`Error fetching questions for video lesson ${videoLessonId}:`, error);
    throw new Error(
      `Failed to fetch questions for video lesson ${videoLessonId}.`
    );
  }
}

/**
 * Get CSRF token from cookies
 */
function getCSRFToken() {
  const name = 'csrftoken';
  let cookieValue = null;
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.substring(0, name.length + 1) === (name + '=')) {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
}

/**
 * Submit answer for validation
 * @param {number} questionId - The ID of the question
 * @param {string|Object} answer - The user's answer (string for text/yesNo, object for dragDrop)
 * @param {string} questionType - Type of question ('yesNo', 'textVoice', or 'dragDrop')
 * @returns {Promise<Object>} Validation result
 */
export async function submitQuestionAnswer(questionId, answer, questionType = 'yesNo') {
  try {
    let requestBody;

    if (questionType === 'dragDrop') {
      // For drag & drop, answer should be an object with gap_id -> word_id mappings
      requestBody = {
        text_answer: JSON.stringify({ answer })
      };
    } else {
      // For text/voice and yes/no questions, send as plain text
      requestBody = {
        text_answer: answer
      };
    }

    // Get CSRF token
    const csrfToken = getCSRFToken();

    console.log('Sending request to backend:', {
      questionId,
      requestBody,
      questionType,
      csrfToken: csrfToken ? 'present' : 'missing'
    });

    const headers = {
      'Content-Type': 'application/json',
    };

    // Add CSRF token to headers if available
    if (csrfToken) {
      headers['X-CSRFToken'] = csrfToken;
    }

    const response = await axios.post(
      `/api/v1/questions/${questionId}/answers/`,
      requestBody,
      {
        withCredentials: true,
        headers: headers
      }
    );

    console.log('Backend response:', response.data);

    // Transform backend response to match frontend expectations
    const transformedResponse = {
      ...response.data,
      result: response.data.answer_status, // Map answer_status to result for frontend compatibility
      feedback: response.data.ai_feedback || "" // Include AI feedback for explanations
    };

    console.log('Transformed response for frontend:', transformedResponse);
    return transformedResponse;
  } catch (error) {
    console.error(`Error submitting answer for question ${questionId}:`, error);
    console.error('Error details:', error.response?.data);

    // More detailed error logging
    if (error.response?.status === 403) {
      console.error('403 Forbidden - Possible CSRF issue or authentication problem');
      console.error('Response data:', error.response.data);
    }

    throw new Error(
      error.response?.data?.detail ||
      error.response?.data?.message ||
      `Failed to submit answer for question ${questionId}.`
    );
  }
}

/**
 * Transform backend questions to the format expected by QuestionCard
 * @param {Array} backendQuestions - Questions from backend API
 * @returns {Array} Transformed questions
 */
export function transformBackendQuestions(backendQuestions) {
  return backendQuestions.map(question => {
    if (question.question_type === "Yes/No") {
      return {
        id: question.id,
        type: 'yesNo',
        text: question.text,
        correctAnswer: null, // Backend validates answers
        hint: question.hint || "", // Use backend hint or empty string
        explanation: {
          yes: "Based on the video content, this answer is correct.",
          no: "Based on the video content, this answer is correct."
        }
      };
    } else if (question.question_type === "Open-ended") {
      return {
        id: question.id,
        type: 'textVoice',
        text: question.text,
        hint: question.hint || "", // Use backend hint or empty string
        sampleAnswer: "", // Backend validates answers
        explanation: "Your answer will be evaluated based on the video content."
      };
    } else if (question.question_type === "Word Order") {
      // Transform word order questions to drag & drop format
      return {
        id: question.id,
        type: 'dragDrop',
        text: question.text,
        hint: question.hint || "",
        // Store backend-specific data for word order questions
        templateSegments: question.template_segments,
        draggableWords: question.draggable_words,
        // Generate sentence template for display
        sentence: generateSentenceTemplate(question.template_segments),
        // Extract just the word content for draggable words
        words: question.draggable_words.map(w => w.content),
        // We'll determine correct positions from the template
        correctPositions: [], // Will be filled by frontend logic
        explanation: "Your word order will be evaluated based on the video content."
      };
    } else if (question.question_type === "Translation Match") {
      // New type: user sees an English phrase and picks the correct translation
      return {
        id: question.id,
        type: 'translationMatch',
        text: question.text, // The English phrase/word to translate
        hint: question.hint || "",
        answerOptions: question.answer_options || [], // Array of translated options
        explanation: ""
      };
    }

    // Default fallback
    return {
      id: question.id,
      type: 'textVoice',
      text: question.text,
      hint: question.hint || "", // Use backend hint or empty string
      sampleAnswer: "",
      explanation: "Your response will be checked against the video content."
    };
  });
}

/**
 * Generate sentence template with gaps from backend template segments
 * @param {Array} templateSegments - Template segments from backend
 * @returns {string} Sentence template with ___ for gaps
 */
function generateSentenceTemplate(templateSegments) {
  return templateSegments.map(segment => {
    if (segment.type === 'fixed') {
      return segment.content;
    } else if (segment.type === 'gap') {
      return '___';
    }
    return '';
  }).join(' ');
}